import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "@Reducers/index";
import { isModelPropertiesWiped } from "@Services/utils/modelPropertiesDataDB";

/**
 * Returns `true` when this tab's model-property data for the file was loaded and
 * then wiped from the shared IndexedDB by another tab's Settings > Clear Cache,
 * so a panel can show a "refresh to reload" state instead of an empty/broken view.
 *
 * Owns the load-state gate, then delegates the existence check (including
 * merged-file resolution) to `isModelPropertiesWiped`. Re-checks whenever the
 * file, `dataVersion`, or load flags change. Holds no model data and makes no
 * copies. Consumers that source the id from props/context (e.g. ModelTree) pass
 * it in; others fall back to the route `:fileId` param.
 *
 * For non-React / imperative call sites, use `isModelPropertiesWiped` directly.
 */
export function useModelPropertiesWiped(fileVersionIdArg?: string): boolean {
  const params = useParams<{ fileId?: string }>();
  const fileVersionId = fileVersionIdArg ?? params.fileId ?? "";

  // Used both to short-circuit below (skip the async check when not loaded) and
  // as effect deps so the check re-runs whenever they change.
  const isLoaded = useSelector((state: RootState) => state.modelProperties.isLoaded);
  const isLoading = useSelector((state: RootState) => state.modelProperties.isLoading);
  const dataVersion = useSelector((state: RootState) => state.modelProperties.dataVersion);

  const [wiped, setWiped] = useState(false);

  useEffect(() => {
    // Not loaded / mid-load — nothing to detect yet; skip the async check.
    if (!fileVersionId || !isLoaded || isLoading) {
      setWiped(false);
      return;
    }
    // Latest-wins: a newer run (file switch / dataVersion bump) invalidates this one.
    let active = true;
    isModelPropertiesWiped(fileVersionId).then((result) => {
      if (active) setWiped(result);
    });
    return () => {
      active = false;
    };
  }, [fileVersionId, dataVersion, isLoaded, isLoading]);

  return wiped;
}
